import { ElementRef, inject, Injectable, signal, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, lastValueFrom, take, Subject, takeUntil, combineLatest, filter, distinctUntilChanged, map } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgZone } from '@angular/core';
import { RightSlideoutPanelComponent } from '../../pages/common/right-slideout-panel/right-slideout-panel.component';
import { SnackbarComponent } from '../components/snackbar/snackbar.component';
import { NgxPermissionsService } from 'ngx-permissions';
import { AccessControlService } from '../../core/services/access-control.service';
import { isEmpty } from '../utils/common.utils';

/**
 * Central shared service for managing application-wide state, user context, UI components, and utilities
 * Handles user authentication, permissions, navigation, dialogs, notifications, and device detection
 * Implements proper cleanup via OnDestroy lifecycle
 */
@Injectable({
  providedIn: 'root',
})
export class SharedService implements OnDestroy {
  private readonly unSub$ = new Subject<void>();
  
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbar = inject(MatSnackBar);
  readonly slideOutDialog = inject(MatDialog);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly accessControl = inject(AccessControlService);
  readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly zone = inject(NgZone);

  private readonly dialogRefs = new Map<string, MatDialogRef<any>>();
  
  isLoggedIn = false;
  offsetDate: string = '';

  private readonly _summaryView = new BehaviorSubject<boolean>(true);
  private readonly _loadPermissions = new BehaviorSubject<boolean>(false);
  private readonly _modules = new BehaviorSubject<any>({} as any);
  private readonly _adminSettings = new BehaviorSubject<any>(null);
  private readonly _isDarkMode = new BehaviorSubject<boolean>(false);
  private readonly _isSuperAdmin = new BehaviorSubject<boolean>(false);
  private readonly _isEAUser = new BehaviorSubject<boolean>(false);
  private readonly _isCustAdmin = new BehaviorSubject<boolean>(false);
  // private readonly _isActiveUser = new BehaviorSubject<boolean>(true);
  private readonly _customersList = new BehaviorSubject<any[]>([]);
  private readonly _user = new BehaviorSubject<any>({} as any);
  private readonly _siteName = new BehaviorSubject<string>('');
  private readonly slideOutCloseSubject = new BehaviorSubject<any>(null);

  private readonly _hasAgreementSigned = new BehaviorSubject<boolean>(false);
  
  private _customerId: number = 0;
  private readonly _customerId$ = new BehaviorSubject<number>(0);
  /** Emits the latest customerId; use filter(Boolean) to wait until it is loaded. */
  readonly customerId$ = this._customerId$.asObservable();
  private _siteId: number = 0;
  private _subStationId: number = 0;
  private _assetId: number = 0;
  private _subAssetId: number = 0;
  private _feederNo: string = '';
  private _peakUserCountInfo: string = '';
  private _planExpiredInfo: string = '';
  customerIdforDLB: number = 0;

  readonly userSignal = signal<any>({} as any);
  readonly userProfilePicUrl = signal<string | null>(null);
  readonly customerLogoUrl = signal<string | null>(null);

  readonly isMobile = this.deviceDetectorService.isMobile();
  readonly isTablet = this.deviceDetectorService.isTablet();
  readonly isDesktopDevice = this.deviceDetectorService.isDesktop();
  readonly currentDeviceType = this.deviceDetectorService.deviceType;

  readonly getSummaryView = this._summaryView.asObservable();
  readonly permissionsLoaded = this._loadPermissions.asObservable();
  readonly getModule = this._modules.asObservable();
  readonly getAdminSettings = this._adminSettings.asObservable();
  readonly isDarkMode$ = this._isDarkMode.asObservable();
  readonly isSuperAdmin$ = this._isSuperAdmin.asObservable();
  readonly isEAUser$ = this._isEAUser.asObservable();
  readonly isCustAdmin$ = this._isCustAdmin.asObservable();
  // readonly isActiveUser$ = this._isActiveUser.asObservable();
  readonly getCstomersList = this._customersList.asObservable();
  readonly getUser = this._user.asObservable();
  readonly getSiteName = this._siteName.asObservable();
  readonly slideOutClose$ = this.slideOutCloseSubject.asObservable();


  private _isActiveUser?: boolean;

private readonly isActiveUserSubject = new Subject<boolean>();

get isActiveUser$(): Observable<boolean> {
  return this.isActiveUserSubject.asObservable();
}

get isActiveUser(): boolean | undefined {
  return this._isActiveUser;
}

set isActiveUser(value: boolean | undefined) {
  this._isActiveUser = value;

  if (value !== undefined) {
    this.isActiveUserSubject.next(value);
  }
}

  constructor() {
    try {
      this.initializeUserSubscription();
    } catch (error) {
      console.error('Error initializing SharedService:', error);
    }

    this.getUser.subscribe((user) => {
      this.offsetDate = user?.customer?.utcOffset || '';
    });
  }

  /**
   * Initializes user subscription to sync offsetDate on user changes
   * Properly unsubscribes on service destroy via takeUntil
   * @private
   */
  private initializeUserSubscription(): void {
    try {
      this.getUser.pipe(takeUntil(this.unSub$)).subscribe({
        next: (user) => {
          try {
            if (!isEmpty(user?.customer?.utcOffset)) {
              this.offsetDate = user.customer.utcOffset;
            }
          } catch (error) {
            console.error('Error processing user offset date:', error);
          }
        },
        error: (error) => {
          console.error('Error in user subscription:', error);
        },
      });
    } catch (error) {
      console.error('Error initializing user subscription:', error);
    }
  }

  set summaryView(value: boolean) {
    try {
      this._summaryView.next(value);
    } catch (error) {
      console.error('Error setting summaryView:', error);
    }
  }
  get summaryView(): boolean {
    return this._summaryView.getValue();
  }

  set loadPermissions(value: any) {
    try {
      this._loadPermissions.next(value);
    } catch (error) {
      console.error('Error setting loadPermissions:', error);
    }
  }
  get loadPermissions(): any {
    return this._loadPermissions.getValue();
  }

  set modules(value: any) {
    try {
      this._modules.next(!isEmpty(value) ? value : {});
    } catch (error) {
      console.error('Error setting modules:', error);
    }
  }
  get modules(): any {
    return this._modules.getValue();
  }

  set adminSettings(value: any) {
    try {
      this._adminSettings.next(value);
    } catch (error) {
      console.error('Error setting adminSettings:', error);
    }
  }
  get adminSettings(): any {
    return this._adminSettings.getValue();
  }

  set isDarkMode(value: boolean) {
    try {
      this._isDarkMode.next(value);
    } catch (error) {
      console.error('Error setting isDarkMode:', error);
    }
  }
  get isDarkMode(): boolean {
    return this._isDarkMode.getValue();
  }

  set isSuperAdmin(value: boolean) {
    try {
      this._isSuperAdmin.next(value);
    } catch (error) {
      console.error('Error setting isSuperAdmin:', error);
    }
  }
  get isSuperAdmin(): boolean {
    return this._isSuperAdmin.getValue();
  }

  set isEAUser(value: boolean) {
    try {
      this._isEAUser.next(value);
    } catch (error) {
      console.error('Error setting isEAUser:', error);
    }
  }
  get isEAUser(): boolean {
    return this._isEAUser.getValue();
  }

  set isCustAdmin(value: boolean) {
    try {
      this._isCustAdmin.next(value);
    } catch (error) {
      console.error('Error setting isCustAdmin:', error);
    }
  }
  get isCustAdmin(): boolean {
    return this._isCustAdmin.getValue();
  }

  set customersList(value: any[]) {
    try {
      this._customersList.next(Array.isArray(value) ? value : []);
    } catch (error) {
      console.error('Error setting customersList:', error);
    }
  }
  get customersList(): any[] {
    return this._customersList.getValue();
  }

  /**
   * Refreshes customers list from API using AccessControlService
   * Updates customersList BehaviorSubject if customers exist in response
   * @returns Promise that resolves when refresh completes
   */
  async refreshCustomersListFromApi(): Promise<void> {
    try {
      const result = await lastValueFrom(this.accessControl.getAdminSettings().pipe(take(1)));
      if (!isEmpty(result?.customers) && Array.isArray(result.customers)) {
        this.customersList = result.customers;
      }
    } catch (error) {
      console.error('Error refreshing customers list from API:', error);
    }
  }

  set user(value: any) {
    try {
      this._user.next(value);
      this.userSignal.set(value);
    } catch (error) {
      console.error('Error setting user:', error);
    }
  }
  get user(): any {
    return this._user.getValue();
  }

  /**
   * Applies user account context globally and synchronizes all related flags
   * Updates isSuperAdmin, isEAUser, isCustAdmin, isActiveUser, modules, and customerId
   * Refreshes customers list if no customerId is available
   * @param user - Account payload from access-control API
   */
  applyUserContext(user: any): void {
    try {
      if (isEmpty(user)) {
        console.warn('applyUserContext: user is empty');
        return;
      }

      this.user = user;
      this.isActiveUser = !!user?.active;
      this.isSuperAdmin = !!user?.isSuperAdmin;
      this.isEAUser = !!user?.isEaUser;
      this.isCustAdmin = !!user?.isCustAdmin;
      this.modules = user?.entityAccessDetails || {};
      this.customerId = user?.customerId ?? 0;
      this.hasAgreementSigned = !!user?.hasAgreementSigned;

      if (!this.customerId) {
        void this.refreshCustomersListFromApi().catch((error) => {
          console.error('Error refreshing customers after context apply:', error);
        });
      }
    } catch (error) {
      console.error('Error in applyUserContext:', error);
    }
  }

  set siteName(value: string) {
    try {
      this._siteName.next(!isEmpty(value) ? value : '');
    } catch (error) {
      console.error('Error setting siteName:', error);
    }
  }
  get siteName(): string {
    return this._siteName.getValue();
  }

  set customerId(value: number) {
    try {
      if (!value && !this.isSuperAdmin && !this.isEAUser) {
        value = this.user?.customer?.id || 0;
      }
      this._customerId = value;
      this._customerId$.next(value);
      this.persistState('customer', value);
    } catch (error) {
      console.error('Error setting customerId:', error);
    }
  }
  get customerId(): number {
    return this._customerId;
  }

  set siteId(value: number) {
    try {
      this._siteId = value;
      this.persistState('site', value);
    } catch (error) {
      console.error('Error setting siteId:', error);
    }
  }
  get siteId(): number {
    return this._siteId;
  }

  set subStationId(value: number) {
    try {
      this._subStationId = value;
      this.persistState('subStation', value);
    } catch (error) {
      console.error('Error setting subStationId:', error);
    }
  }
  get subStationId(): number {
    return this._subStationId;
  }

  set assetId(value: number) {
    try {
      this._assetId = value;
      this.persistState('asset', value);
    } catch (error) {
      console.error('Error setting assetId:', error);
    }
  }
  get assetId(): number {
    return this._assetId;
  }

  set subAssetId(value: number) {
    try {
      this._subAssetId = value;
      this.persistState('subAsset', value);
    } catch (error) {
      console.error('Error setting subAssetId:', error);
    }
  }
  get subAssetId(): number {
    return this._subAssetId;
  }

  set feederNo(value: string) {
    try {
      this._feederNo = !isEmpty(value) ? value : '';
      this.persistState('feederNo', value);
    } catch (error) {
      console.error('Error setting feederNo:', error);
    }
  }
  get feederNo(): string {
    return this._feederNo;
  }

  set peakUserCountInfo(val: string) {
    try {
      this._peakUserCountInfo = !isEmpty(val) ? val : '';
    } catch (error) {
      console.error('Error setting peakUserCountInfo:', error);
    }
  }
  get peakUserCountInfo(): string {
    return this._peakUserCountInfo;
  }

  set planExpiredInfo(val: string) {
    try {
      this._planExpiredInfo = !isEmpty(val) ? val : '';
    } catch (error) {
      console.error('Error setting planExpiredInfo:', error);
    }
  }
  get planExpiredInfo(): string {
    return this._planExpiredInfo;
  }

  /**
   * Encodes value to base64 string for safe storage
   * @param id - Value to encode (number or string)
   * @returns Base64 encoded string
   */
  encodeId(id: number | string): string {
    try {
      return btoa(!isEmpty(id) ? id.toString() : '');
    } catch (error) {
      console.error('Error encoding id:', error);
      return '';
    }
  }

  /**
   * Decodes base64 string, returns original value with fallback to input if decode fails
   * @param encoded - Base64 encoded string to decode
   * @returns Decoded string or original input on error
   */
  decodeId(encoded: string): string {
    try {
      if (isEmpty(encoded)) {
        return '';
      }
      return atob(encoded);
    } catch (error) {
      console.warn('Error decoding id, returning original:', error);
      return encoded;
    }
  }

  /**
   * Persists value to localStorage with encoding
   * Encodes value then stores as JSON string under specified key
   * @param key - localStorage key name
   * @param value - Value to persist (will be encoded)
   */
  persistState(key: string, value: any): void {
    try {
      if (isEmpty(key)) {
        console.warn('persistState: key is empty');
        return;
      }
      const encoded = this.encodeId(value);
      localStorage.setItem(key, JSON.stringify(encoded));
    } catch (error) {
      console.error('Error persisting state:', error);
    }
  }

  /**
   * Restores value from localStorage with decoding
   * Parses JSON string and decodes value, returns default if missing
   * @param key - localStorage key to retrieve
   * @param defaultValue - Default value if key not found (defaults to null)
   * @returns Decoded value from storage or default value
   */
  restoreState(key: string, defaultValue: any = null): any {
    try {
      if (isEmpty(key)) {
        console.warn('restoreState: key is empty');
        return defaultValue;
      }
      const val = localStorage.getItem(key);
      return !isEmpty(val) ? this.decodeId(JSON.parse(val)) : defaultValue;
    } catch (error) {
      console.error('Error restoring state:', error);
      return defaultValue;
    }
  }

  /**
   * Trims all whitespace from string
   * Removes all space characters using regex replacement
   * @param string - String to trim
   * @returns String with all whitespace removed
   */
  stringTrim(string: string): string {
    try {
      return !isEmpty(string) ? string.replace(/\s/g, '') : '';
    } catch (error) {
      console.error('Error trimming string:', error);
      return string || '';
    }
  }

  /**
   * Checks if currently logged-in user matches provided user
   * Normalizes emails for case-insensitive comparison
   * @param user - User object, email string, or null to compare against logged-in user
   * @returns True if emails match, false otherwise
   */
  isLoggedInUser(user: { email?: string; emailAddress?: string } | string | null | undefined): boolean {
    try {
      const loggedInUserEmail = this.normalizeEmail(this.user?.email || this.user?.emailAddress);
      if (isEmpty(loggedInUserEmail)) {
        return false;
      }

      const candidateEmail = this.normalizeEmail(
        typeof user === 'string' ? user : user?.email || user?.emailAddress
      );

      return loggedInUserEmail === candidateEmail;
    } catch (error) {
      console.error('Error checking if logged-in user:', error);
      return false;
    }
  }

  /**
   * Normalizes email to lowercase and trimmed format
   * Handles null/undefined by returning empty string
   * @param email - Email string to normalize
   * @returns Normalized email in lowercase
   * @private
   */
  private normalizeEmail(email: string | null | undefined): string {
    try {
      return !isEmpty(email) ? (email || '').trim().toLowerCase() : '';
    } catch (error) {
      console.error('Error normalizing email:', error);
      return '';
    }
  }

  /**
   * Navigates to specified route if not already on that route
   * Checks router URL before navigation to avoid unnecessary navigation
   * @param node - Node object with routelink property
   */
  navigateTo(node: any): void {
    try {
      if (isEmpty(node) || isEmpty(node.routelink)) {
        console.warn('navigateTo: node or routelink is empty');
        return;
      }

      if (this.router.url === node.routelink) {
        return;
      }

      void this.router.navigate([node.routelink]).catch((error) => {
        console.error('Error navigating to route:', error);
      });
    } catch (error) {
      console.error('Error in navigateTo:', error);
    }
  }

  /**
   * Extracts all route parameters from router state
   * Recursively traverses ActivatedRouteSnapshot hierarchy
   * @param router - Angular Router instance
   * @returns Object with all route parameters from all levels
   */
  extractAllRouteParams(router: Router): any {
    try {
      if (isEmpty(router)) {
        console.warn('extractAllRouteParams: router is empty');
        return {};
      }

      const params: any = {};
      let route: ActivatedRouteSnapshot | null = router.routerState.snapshot.root;

      while (route) {
        if (!isEmpty(route.params)) {
          Object.keys(route.params).forEach((key) => {
            params[key] = route?.params[key];
          });
        }
        route = route.firstChild;
      }

      return params;
    } catch (error) {
      console.error('Error extracting route params:', error);
      return {};
    }
  }

  /**
   * Opens slide-out panel with specified module and type
   * Stores dialog reference for later closing
   * Emits slideOutClose$ event when dialog closes
   * @param module - Module identifier for the panel
   * @param type - Type/action identifier for the panel
   * @param id - Optional entity ID for the panel context
   * @param element - Optional UI element reference
   */
  openSlideOutPanel(module: string, type: string, id: any = null, element: any = null): void {
    try {
      if (isEmpty(module) || isEmpty(type)) {
        console.warn('openSlideOutPanel: module or type is empty');
        return;
      }

      const dialogRef = this.slideOutDialog.open(RightSlideoutPanelComponent, {
        panelClass: 'slide-out-panel-dialog',
        disableClose: true,
        data: { module, type, id, element },
      });

      this.dialogRefs.set(module, dialogRef);

      dialogRef.afterClosed().subscribe({
        next: (result) => {
          try {
            this.dialogRefs.delete(module);
            this.slideOutCloseSubject.next({ module, result });
          } catch (error) {
            console.error('Error processing slide-out panel close:', error);
          }
        },
        error: (error) => {
          console.error('Error in slide-out panel afterClosed:', error);
          this.dialogRefs.delete(module);
        },
      });
    } catch (error) {
      console.error('Error opening slide-out panel:', error);
    }
  }

  /**
   * Closes slide-out panel for specified module
   * Resolves dialog with provided result value
   * @param module - Module identifier of panel to close
   * @param result - Optional result value to return from dialog
   */
  closeSlideOutPanel(module: string, result: any = null): void {
    try {
      if (isEmpty(module)) {
        console.warn('closeSlideOutPanel: module is empty');
        return;
      }

      const dialogRef = this.dialogRefs.get(module);
      if (!isEmpty(dialogRef)) {
        dialogRef.close(result);
      }
    } catch (error) {
      console.error('Error closing slide-out panel:', error);
    }
  }

  /**
   * Shows Material snackbar notification with type and message
   * Displays at top-center for 3 seconds using SnackbarComponent
   * @param type - Notification type code (success, error, warning, info)
   * @param message - Notification message text
   */
  showSnackbar(type: number, message: string): void {
    try {
      if (isEmpty(message)) {
        console.warn('showSnackbar: message is empty');
        return;
      }

      this.snackbar.openFromComponent(SnackbarComponent, {
        data: {
          type,
          message,
        },
        horizontalPosition: 'center',
        verticalPosition: 'top',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error showing snackbar:', error);
    }
  }

  /**
   * Converts local date to UTC format
   * Extracts UTC components and formats as ISO string
   * @param localDate - Local date string to convert
   * @returns UTC date in ISO format (YYYY-MM-DDTHH:mm:ss)
   */
  convertLocalToUTC(localDate: string): string {
    try {
      if (isEmpty(localDate)) {
        console.warn('convertLocalToUTC: localDate is empty');
        return '';
      }

      const localDateObj = new Date(localDate);

      if (isNaN(localDateObj.getTime())) {
        console.warn('convertLocalToUTC: invalid date');
        return '';
      }

      const year = localDateObj.getUTCFullYear();
      const month = String(localDateObj.getUTCMonth() + 1).padStart(2, '0');
      const day = String(localDateObj.getUTCDate()).padStart(2, '0');
      const hours = String(localDateObj.getUTCHours()).padStart(2, '0');
      const minutes = String(localDateObj.getUTCMinutes()).padStart(2, '0');
      const seconds = String(localDateObj.getUTCSeconds()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error converting local to UTC:', error);
      return '';
    }
  }

  /**
   * Converts UTC date to local time format
   * Adds Z suffix to interpret as UTC, then extracts local components
   * @param utcDate - UTC date string to convert (without Z suffix)
   * @returns Local time in ISO format (YYYY-MM-DDTHH:mm:ss)
   */
  convertUTCToLocal(utcDate: string): string {
    try {
      if (isEmpty(utcDate)) {
        console.warn('convertUTCToLocal: utcDate is empty');
        return '';
      }

      const utcDateObj = new Date(utcDate + 'Z');

      if (isNaN(utcDateObj.getTime())) {
        console.warn('convertUTCToLocal: invalid date');
        return '';
      }

      const year = utcDateObj.getFullYear();
      const month = String(utcDateObj.getMonth() + 1).padStart(2, '0');
      const day = String(utcDateObj.getDate()).padStart(2, '0');
      const hours = String(utcDateObj.getHours()).padStart(2, '0');
      const minutes = String(utcDateObj.getMinutes()).padStart(2, '0');
      const seconds = String(utcDateObj.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error converting UTC to local:', error);
      return '';
    }
  }

  /**
   * Generates array of 48 time slots (30-minute intervals) for a 24-hour period
   * Formats times in 12-hour format with AM/PM
   * @returns Array of time slot strings (e.g., ['12:00 AM', '12:30 AM', ...])
   */
  generateTimeSlots(): string[] {
    try {
      const timeSlots: string[] = [];
      const startTime = new Date();
      startTime.setHours(0, 0, 0, 0);

      for (let i = 0; i < 48; i++) {
        const hours = startTime.getHours();
        const minutes = startTime.getMinutes();
        const period = hours < 12 ? 'AM' : 'PM';
        let formattedHours: string | number = hours % 12 === 0 ? 12 : hours % 12;
        formattedHours = formattedHours < 10 ? '0' + formattedHours : formattedHours;
        const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
        const timeSlot = `${formattedHours}:${formattedMinutes} ${period}`;
        timeSlots.push(timeSlot);
        startTime.setMinutes(startTime.getMinutes() + 30);
      }

      return timeSlots;
    } catch (error) {
      console.error('Error generating time slots:', error);
      return [];
    }
  }

  /**
   * Sets site ID for DLB (Dashboard List Box) or similar context
   * Stores value in customerIdforDLB property
   * @deprecated Use siteId property instead for consistency
   * @param id - Site ID to set
   */
  setCustomerIdDLB(customerid: number): void {
    try {
      if (typeof customerid === 'number') {
        this.customerIdforDLB = customerid;
      }
    } catch (error) {
      console.error('Error setting customer ID for DLB:', error);
    }
  }

  /**
   * Gets site ID for DLB context
   * @deprecated Use customerId property instead for consistency
   * @returns Stored customer ID for DLB
   */
  getCustomerIdDLB(): number {
    try {
      return this.customerIdforDLB || 0;
    } catch (error) {
      console.error('Error getting customer ID for DLB:', error);
      return 0;
    }
  }

  /**
   * Clears peak-user-count information message
   * Resets internal peak user count banner message
   */
  clearPeakUserCountInfo(): void {
    try {
      this.peakUserCountInfo = '';
    } catch (error) {
      console.error('Error clearing peak user count info:', error);
    }
  }

  clearPlanExpiredInfo(): void {
    try {
      this.planExpiredInfo = '';
    } catch (error) {
      console.error('Error clearing plan expired info:', error);
    }
  }

  /**
   * Checks if user has at least one of the required permissions
   * Uses ngx-permissions service to validate
   * @param requiredPermissions - Array of permission keys to check
   * @returns True if user has any of the required permissions
   */
  hasPermission(requiredPermissions: string[]): boolean {
    try {
      if (isEmpty(requiredPermissions) || !Array.isArray(requiredPermissions)) {
        console.warn('hasPermission: requiredPermissions is empty or not array');
        return false;
      }

      const permissions = this.ngxPermissionsService.getPermissions();
      if (isEmpty(permissions)) {
        return false;
      }

      return requiredPermissions.some((permission) => permissions[permission]);
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  }

  /**
   * Renders image blob to canvas element with optional width styling
   * Uses createImageBitmap for efficient rendering
   * @param blob - Image blob to render
   * @param canvasEl - Canvas element reference
   * @param styleWidth - Optional width in pixels for canvas styling
   * @param isUserProfile - Optional flag for profile-specific styling (height: 100%)
   * @returns Promise that resolves when rendering completes
   */
  async render(
    blob: Blob,
    canvasEl: ElementRef<HTMLCanvasElement>,
    styleWidth: number,
    isUserProfile: boolean = false
  ): Promise<void> {
    try {
      if (!blob || !canvasEl?.nativeElement) {
        console.warn('render: blob or canvasEl is empty');
        return;
      }

      const canvas = canvasEl.nativeElement;
      if (!canvas) {
        console.warn('render: canvas element not found');
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.warn('render: canvas context not available');
        return;
      }

      const bmp = await createImageBitmap(blob);

      canvas.width = bmp.width;
      canvas.height = bmp.height;

      if (styleWidth && styleWidth > 0) {
        canvas.style.width = styleWidth + 'px';
        canvas.style.height = isUserProfile ? '100%' : 'auto';
      }

      ctx.drawImage(bmp, 0, 0);
      bmp.close?.();
    } catch (error) {
      console.error('Error rendering image to canvas:', error);
    }
  }

  /**
   * Clears canvas element and resets dimensions
   * Removes all drawn content and sets width/height to 0
   * @param canvasEl - Canvas element reference to clear
   */
  clearCanvas(canvasEl: ElementRef<HTMLCanvasElement>): void {
    try {
      if (!canvasEl?.nativeElement) {
        console.warn('clearCanvas: canvasEl is empty');
        return;
      }

      const canvas = canvasEl.nativeElement;
      if (!canvas) {
        console.warn('clearCanvas: canvas element not found');
        return;
      }

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      canvas.width = 0;
      canvas.height = 0;
    } catch (error) {
      console.error('Error clearing canvas:', error);
    }
  }

  /**
   * Checks if currently logged-in user is TechnoDay customer
   * Matches against customer ID 926 or customer code '01020304'
   * @returns True if user is TechnoDay customer
   */
  isTechnoDayCustomer(): boolean {
    try {
      const loggedInUserCustomer = this.user?.customer;
      if (isEmpty(loggedInUserCustomer)) {
        return false;
      }

      const userCustomerId = loggedInUserCustomer?.id;
      const userCustomerCode = loggedInUserCustomer?.customerCode;

      return (
        userCustomerId === 926 ||
        userCustomerCode === '01020304' ||
        this.customerId === 926
      );
    } catch (error) {
      console.error('Error checking if TechnoDay customer:', error);
      return false;
    }
  }

  /**
   * Lifecycle hook - cleanup on service destroy
   * Completes subscription chain and cleans up all listeners
   */
  ngOnDestroy(): void {
    try {
      this.unSub$.next();
      this.unSub$.complete();
      this.dialogRefs.clear();
    } catch (error) {
      console.error('Error in ngOnDestroy:', error);
    }
  }

  /**
   * Checks if value is empty or null/undefined
   * Handles various data types: strings, objects, arrays, numbers
   * Used for all empty validation in shared service
   * @param value - Any value to check
   * @returns True if value is considered empty
 */
  isEmpty(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim().length === 0;
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }

    return false;
  }
  
  /**
   * Abstract setter for agreement signed status
   * Updates the internal BehaviorSubject with the provided value
   * @param val - Boolean indicating if the agreement has been signed
   */
  set hasAgreementSigned(val: boolean) {
    this._hasAgreementSigned.next(!!val);
  }
  /**
   * Abstract getter for agreement signed status
   * Retrieves the current value from the internal BehaviorSubject
   * @returns Boolean indicating if the agreement has been signed
   */
  get hasAgreementSigned(): boolean {
    return this._hasAgreementSigned.getValue();
  }

  /**
   * Abstract observable indicating if the agreement has been signed
   * Emits true if the agreement is signed and the user is active
   */
  hasAgreementSigned$: Observable<boolean> = combineLatest([
    this._hasAgreementSigned.asObservable(),
    this.isActiveUser$,
    this._user.asObservable(),
  ]).pipe(
    filter(([, isActive, user]) => isActive && this.isValidUserContext(user)),
    map(([hasAgreementSigned]) => hasAgreementSigned),
    distinctUntilChanged()
  );

  /**
   * Validates if the provided user context is valid
   * Checks for presence of essential user properties like id, email, or emailAddress
   * @param user - User object to validate
   * @returns True if user context is valid
   */
  private isValidUserContext(user: any): boolean {
    return !!(user?.id || user?.emailAddress);
  }
}

